import React, { useState, useMemo } from "react";
import { StyleSheet, Platform, View, Dimensions } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { toggleOption } from "../../store/slices/pulseCheckSlice";
import { useTheme } from "../../theme";
import { ThemeColors } from "../../theme/types";
import LHSectionHeader from "../../components/LHSectionHeader";
import LHOptionCard from "../../components/LHOptionCard";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import PulseCheckActivityImageUpload from "./PulseCheckActivityImageUpload";
import { getPulseActivityColor } from "../../utils/categoryColors";
import { Text, View as RNView } from "react-native";

interface Option {
  id: string;
  label: string;
  SPARC_Category?: string; // Adding SPARC_Category from backend
  Material?: string; // PDF link from backend
  Pallet_Type?: string; // Add Pallet_Type field for the second label
}

interface PulseCheckActivityProps {
  title: string;
  options: Option[];
}

const PulseCheckActivity: React.FC<PulseCheckActivityProps> = ({
  title,
  options,
}) => {
  const dispatch = useDispatch();
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const selectedOptions = useSelector(
    (state: RootState) => state.pulseCheck.selectedOptions
  );
  const pulseState = useSelector(
    (state: RootState) => state.pulseCheck.pulseState
  );
  const pulseImages = useSelector(
    (state: RootState) => state.pulseCheck.pulseImages
  );

  // State for image upload modal
  const [imageUploadModalVisible, setImageUploadModalVisible] =
    useState<boolean>(false);
  const [selectedActivity, setSelectedActivity] = useState<Option | null>(null);

  const handleToggleOption = (option: Option): void => {
    dispatch(
      toggleOption({
        id: option.id,
        label: option.label,
        Pallet_Type: option.Pallet_Type,
      })
    );
  };

  const handleCameraPress = (option: Option): void => {
    // Set the selected activity and open the modal
    setSelectedActivity(option);
    setImageUploadModalVisible(true);
  };

  const handleCloseModal = (): void => {
    setImageUploadModalVisible(false);
  };

  // Use the utility function for header background colors
  const getHeaderBackgroundColor = (title: string) => {
    return getPulseActivityColor(title);
  };

  // Get window dimensions for responsive layout
  const windowWidth = Dimensions.get("window").width;
  const isMobile = Platform.OS === "ios" && windowWidth < 768;

  return (
    <View style={styles.cardContainer}>
      <LHSectionHeader
        title={title}
        containerStyle={{
          ...styles.sectionHeader,
          backgroundColor: getHeaderBackgroundColor(title),
        }}
        textStyle={styles.header1text}
        // icon={<MaterialIcons name="assignment" size={20} color="white" />}
        rightContent={
          <Text style={styles.categoryText}>
            {options[0]?.SPARC_Category || "SAFETY"}
          </Text>
        }
      />
      <View style={[styles.optionGrid, isMobile && styles.optionGridMobile]}>
        {options.map((option) => (
          <LHOptionCard
            key={option.id}
            title={option.label}
            selected={selectedOptions.some((item) => item.id === option.id)}
            onPress={() => handleToggleOption(option)}
            secondLabel={option.Pallet_Type} // Add Pallet_Type as second label
            secondLabelColor="rgb(113 88 116)" // Orange color for Pallet_Type labels
            tagColor="#669DD7"
            pdfLink={option.Material} // Add PDF link if Material field exists
            disabled={pulseState === "completed"}
            onCameraPress={() => handleCameraPress(option)}
            hasUploadedImage={pulseImages.some(
              (img) => img.observationId === option.id
            )}
          />
        ))}
      </View>

      {/* Image Upload Modal */}
      {selectedActivity && (
        <PulseCheckActivityImageUpload
          visible={imageUploadModalVisible}
          onClose={handleCloseModal}
          activityId={selectedActivity.id}
          activityLabel={selectedActivity.label}
        />
      )}
    </View>
  );
};

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    cardContainer: {
      marginBottom: 24,
      borderRadius: 6,
      overflow: 'hidden',
      elevation: 4,
      shadowColor: 'rgba(0, 94, 192, 0.08)',
      shadowOffset: {
        width: 4,
        height: 0,
      },
      shadowOpacity: 1,
      shadowRadius: 24,
    },
    sectionHeader: {
      borderTopLeftRadius: 6,
      borderTopRightRadius: 6,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
    header1text: {
      fontSize: 16,
    },
    categoryText: {
      color: colors.text.inverse,
      fontWeight: "bold",
      fontSize: 14,
      marginLeft: 8,
    },
    optionGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      justifyContent: Platform.OS === "web" ? "flex-start" : "space-between",
      gap: Platform.OS === "web" ? 16 : 6,
      borderBottomLeftRadius: 6,
      borderBottomRightRadius: 6,
      padding: 16,
      backgroundColor: colors.background.surface,
      borderWidth: 1,
      borderTopWidth: 0,
      borderColor: colors.border.default,
    },
    optionGridMobile: {
      flexDirection: "column", // Stack items vertically on mobile
      justifyContent: "flex-start",
      gap: 12, // Increase gap for better spacing
      padding: 12, // Reduce padding for more space
    },
  });

export default PulseCheckActivity;
